import { errorLoggingService } from '../services/errorLoggingService'
import _get from 'lodash/get'
import { formatComponentName } from './errorHandlerUtils'

/**
 * Transform an array of messages from laravel error message bag to be a single html string with breaks
 * @param {Object | Array | String} message
 * @param {String} $glue
 * @example
 * message = { username: ['some error', 'maybe another error'], email: ['another error', 'and another error']}
 * message = { username: 'some error', email: 'and another error'}
 * message = ['some error', 'and another error']
 * message = [['some error'], ['and another error']]
 * message = 'some error'
 * @return { String, Array }
 */
export function flattenErrorMessage (message, $glue = '<br/>') {
  // Check if messages are an Object of array items
  if (typeof message === 'object') {
    const msgArray = message.map((error) => {
      if (typeof error === 'object') {
        return error['detail']
      }

      return error
    }, [])

    return $glue ? msgArray.join($glue) : msgArray
  }
  // its not an object so we just return it.
  return message
}

/**
 * Resolves a boolean / status-array / predicate flag against an error.
 * Shared by `skipErrorReporting` and `guestSilent`, which take the same shapes.
 * @param {Function | Boolean | Array} flag
 * @param {Error} error
 * @param {Number} status - HTTP status, 0 when there is no response
 * @return {Boolean}
 */
function flagMatches (flag, error, status) {
  if (flag === true) return true
  if (Array.isArray(flag)) return flag.includes(status)
  if (typeof flag === 'function') return !!flag(error)
  return false
}

/**
 * Translates a key off the calling component, falling back to English when i18n
 * is unavailable (the util is also called from non-component contexts).
 * @param {Vue} vm - the `this` displayRequestError was called with
 * @param {String} key
 * @param {String} fallback
 * @return {String}
 */
function translate (vm, key, fallback) {
  const t = _get(vm, '$t')
  if (typeof t !== 'function') return fallback
  const translated = t.call(vm, key)

  return typeof translated === 'string' && translated !== key ? translated : fallback
}

/**
 * Displays error messages if they are present in the response
 *
 * T-070: toasts are gated on *passive vs user-initiated*, never on auth state alone.
 * A call site that fired without the visitor doing anything (an on-mount fetch, a
 * background feed) passes `guestSilent`, which drops the toast for anonymous
 * visitors only — they took no action and can take none. Signed-in users keep
 * seeing everything, user-initiated failures always toast, and error reporting
 * below is unaffected in every case.
 *
 * @name displayRequestError
 * @func
 * @param {Error| String} error - Http response from Axios.
 * @param {String | Object} noneStandardErrorMessage - Error message to display if no response from server. Can be settings object or string.
 *   For anonymous visitors this app-owned copy is also preferred over the server's message, which is internal.
 * @param {Number} duration - duration of error message
 * @param {Function | Boolean | Array} skipErrorReporting - allows skipping the error reporting
 * @param {Function | Boolean | Array} guestSilent - suppress the toast for anonymous visitors. Same
 *   boolean / status-array / predicate shapes as skipErrorReporting. Defaults to `false` so an
 *   unmarked call site behaves exactly as it did before. Never suppresses error reporting.
 * @param {Function} [notification] - The notification function to call. Uses ElementUIs Notification
 */
export function displayRequestError (
  error,
  noneStandardErrorMessage = false,
  duration = 10000,
  skipErrorReporting = false,
  guestSilent = false,
  notification) {
  if (!error || error === 'cancel' || error === 'close') return
  notification = notification || this.$notify
  let title = ''
  let message = ''
  let severity = 'error'
  // Check if we have a response from the server
  let response = error.response
  let status = _get(response, 'status', 0)
  const isGuest = !_get(this, '$store.getters.isLoggedIn', false)
  // Passive failure + nobody signed in => no toast. Reporting still happens below.
  const silenceToast = isGuest && flagMatches(guestSilent, error, status)
  // Anonymous visitors never see the server's copy: prefer the caller's app-owned
  // message, else a generic one. Signed-in users keep the detail, it is useful to them.
  const guestSafeMessage = () => typeof noneStandardErrorMessage === 'string'
    ? noneStandardErrorMessage
    : translate(this, 'errors.generic', 'There was an error with the request. Please try again')

  if (response && status < 500) {
    const errorMessage = response.data.message
    const errorFields = response.data.errors
    let dangerouslyUseHTMLString = false
    // If api return errors we add them to the message bag
    if (errorFields && errorFields.length > 0) {
      const flatErrors = flattenErrorMessage(errorFields, '')
      // The field errors themselves stay: they are what tells a visitor how to fix
      // their submission. Only the server-authored heading is swapped out.
      title = isGuest ? guestSafeMessage() : errorMessage
      message = getErrorsString(flatErrors)
      dangerouslyUseHTMLString = true
    } else {
      title = translate(this, 'errors.error', 'An error has occurred')
      message = isGuest ? guestSafeMessage() : errorMessage
    }
    if (!silenceToast) {
      notification.error({
        title,
        duration,
        message,
        dangerouslyUseHTMLString
      })
    }
    if (status === 401) skipErrorReporting = true // We dont want to notify errorLogger when a user is not authenticated
    if (status === 422) severity = 'warning'
  } else if (noneStandardErrorMessage) {
    const msgType = typeof noneStandardErrorMessage
    if (msgType === 'string') {
      if (!silenceToast) {
        notification.error({
          title: translate(this, 'errors.error_title', 'Error'),
          message: noneStandardErrorMessage
        })
      }
    } else if (msgType === 'object') {
      if (!silenceToast) notification(noneStandardErrorMessage)
    }
  } else if (error.message) {
    if (!silenceToast) {
      this.$notify.error({
        title: translate(this, 'errors.error_title', 'Error'),
        // error.message is an axios/runtime string ("Network Error"), internal copy.
        message: isGuest ? guestSafeMessage() : error.message
      })
    }
  }
  // Reporting is deliberately independent of the toast: silencing the visible error
  // must never make the failure invisible to us as well.
  if (flagMatches(skipErrorReporting, error, status)) return
  handleError.call(this, error, {
    serverResponse: response || error.message || error,
    customMessage: noneStandardErrorMessage || ''
  }, severity)
}

/**
 * Handles error reporting to errorLogger
 * @name handleError
 * @function
 * @param {Error} error - Error to notify
 * @param {Object} [meta] - Extra meta data
 * @param {String} [severity] - Severity
 */
export function handleError (error, meta = {}, severity = 'error') {
  let extra = {
    severity: severity,
    metaData: meta,
    componentName: formatComponentName(this)
  }

  errorLoggingService.notify(error, extra)
}

/**
 * Supply an array of errors and creates a vnode tree. Used with displaying errors mostly.
 * @param {Array} flatErrors - Array of errors
 * @return {string}
 */
function getErrorsString (flatErrors) {
  return `<div class="errors">${flatErrors.map(e => `<p>${e}</p>`).join('')}</div>`
}
